/**
 * WC-X3 — unified lead-scoped + user-scoped workplace reminder view model.
 * Normalizes `ops_in_app_notifications` rows and checklist shell meta into one
 * rendering shape for bell, daily checklist, and lead context.
 */
import {
  normalizeReminderScope,
  normalizeReminderStatus,
  type WorkplaceReminderScope,
  type WorkplaceReminderStatus,
} from "./workplaceMessagesPolicy";
import { LEAD_HYGIENE_NUDGE_KIND, parseHygieneTierFromTitle } from "./leadFollowupHygiene";

export const WORKPLACE_REMINDER_NOTIFICATION_KINDS = new Set([
  "lead_reminder",
  "user_reminder",
  "sales_checklist_nudge",
  LEAD_HYGIENE_NUDGE_KIND,
]);

export type WorkplaceReminderSource =
  | "user_reminder"
  | "lead_reminder"
  | "lead_hygiene"
  | "org_nudge"
  | "hygiene_hint";

export type WorkplaceReminderTone = "blue" | "amber" | "indigo" | "neutral";

/** Deterministic deep-link targets — consumers map to in-app navigation. */
export type WorkplaceReminderDeepLink = {
  lead_id?: string;
  message_thread_id?: string;
  checklist_item_id?: string;
  notification_id?: string;
};

export type WorkplaceReminderViewModel = {
  id: string;
  source: WorkplaceReminderSource;
  title: string;
  body: string;
  scope: WorkplaceReminderScope;
  status: WorkplaceReminderStatus;
  dueAt: string | null;
  requiresAck: boolean;
  /** Personal / informational items — dismiss without blocking checklist. */
  dismissible: boolean;
  /** Hygiene nudges support resolve + optional reassign (WC-X4). */
  hygieneActions?: boolean;
  tone: WorkplaceReminderTone;
  deepLink: WorkplaceReminderDeepLink;
  footer?: string;
};

export type RawWorkplaceNotificationRow = {
  id?: string;
  kind?: string;
  title?: string;
  body?: string;
  thread_id?: string | null;
  deal_id?: string | null;
  reminder_scope?: string | null;
  reminder_due_at?: string | null;
  reminder_status?: string | null;
  message_class?: string | null;
  requires_ack?: boolean | null;
  created_at?: string | null;
};

export function isWorkplaceReminderNotificationKind(kind: string | null | undefined): boolean {
  return WORKPLACE_REMINDER_NOTIFICATION_KINDS.has(String(kind || "").trim());
}

/** API stores lead UUID on `deal_id` for reminder rows (pilot convention). */
export function leadRefFromNotificationRow(row: RawWorkplaceNotificationRow): string | null {
  const id = typeof row.deal_id === "string" ? row.deal_id.trim() : "";
  return id || null;
}

export function buildReminderDeepLink(input: {
  notificationId?: string | null;
  threadId?: string | null;
  leadId?: string | null;
  checklistItemId?: string | null;
}): WorkplaceReminderDeepLink {
  const link: WorkplaceReminderDeepLink = {};
  const nid = input.notificationId?.trim();
  const tid = input.threadId?.trim();
  const lid = input.leadId?.trim();
  const cid = input.checklistItemId?.trim();
  if (lid) link.lead_id = lid;
  if (tid) link.message_thread_id = tid;
  if (cid) link.checklist_item_id = cid;
  if (nid) link.notification_id = nid;
  return link;
}

export function normalizeWorkplaceReminderFromNotification(
  row: RawWorkplaceNotificationRow
): WorkplaceReminderViewModel | null {
  const kind = String(row.kind || "").trim();
  if (!isWorkplaceReminderNotificationKind(kind) && kind !== "thread_message") return null;

  const id = String(row.id || "").trim();
  if (!id) return null;

  const scope =
    kind === "sales_checklist_nudge"
      ? "user"
      : normalizeReminderScope(row.reminder_scope ?? (kind === "lead_reminder" ? "lead" : "user"));
  const status = normalizeReminderStatus(row.reminder_status);
  const requiresAck = row.requires_ack === true;
  const leadId = leadRefFromNotificationRow(row);
  const threadId = typeof row.thread_id === "string" ? row.thread_id : null;

  if (kind === "sales_checklist_nudge") {
    return {
      id: `nudge-${id}`,
      source: "org_nudge",
      title: String(row.title || "Org message"),
      body: String(row.body || ""),
      scope: "user",
      status: "open",
      dueAt: null,
      requiresAck: false,
      dismissible: true,
      tone: "blue",
      deepLink: buildReminderDeepLink({
        notificationId: id,
        threadId: threadId ?? id,
        checklistItemId: "daily-checklist",
      }),
      footer: row.created_at ? `From your org · ${formatWhen(row.created_at)} · also in header inbox` : undefined,
    };
  }

  if (kind === LEAD_HYGIENE_NUDGE_KIND) {
    const tier = parseHygieneTierFromTitle(String(row.title || ""));
    return {
      id,
      source: "lead_hygiene",
      title: String(row.title || "Stale lead"),
      body: String(row.body || ""),
      scope: "lead",
      status,
      dueAt: null,
      requiresAck: false,
      dismissible: true,
      hygieneActions: status === "open",
      tone: "indigo",
      deepLink: buildReminderDeepLink({
        notificationId: id,
        leadId: leadId ?? undefined,
      }),
      footer: tier ? `${tier}-day hygiene cadence · does not block daily checklist` : undefined,
    };
  }

  const messageClass = String(row.message_class || "").trim();
  const isPersonal =
    kind === "user_reminder" || messageClass === "personal_followup" || scope === "user";

  return {
    id,
    source: kind === "lead_reminder" ? "lead_reminder" : "user_reminder",
    title: String(row.title || "Reminder"),
    body: String(row.body || ""),
    scope,
    status,
    dueAt: typeof row.reminder_due_at === "string" ? row.reminder_due_at : null,
    requiresAck,
    dismissible: !requiresAck,
    tone: requiresAck ? "amber" : isPersonal || scope === "lead" ? "indigo" : "neutral",
    deepLink: buildReminderDeepLink({
      notificationId: id,
      threadId,
      leadId: scope === "lead" ? leadId ?? undefined : undefined,
      checklistItemId: scope === "user" ? "daily-checklist" : undefined,
    }),
  };
}

export function normalizeOrgManagerNudgeReminder(meta: {
  title: string;
  body: string;
  sent_at: string;
}): WorkplaceReminderViewModel {
  return {
    id: `manager-nudge-${meta.sent_at}`,
    source: "org_nudge",
    title: meta.title,
    body: meta.body,
    scope: "user",
    status: "open",
    dueAt: null,
    requiresAck: false,
    dismissible: true,
    tone: "blue",
    deepLink: { checklist_item_id: "daily-checklist" },
    footer: `From your org · ${formatWhen(meta.sent_at)} · also in header notifications`,
  };
}

export function normalizeHygieneHintReminder(staleDays: number): WorkplaceReminderViewModel {
  return {
    id: `hygiene-hint-${staleDays}`,
    source: "hygiene_hint",
    title: "Lead hygiene",
    body: `Your org flags leads as stale after ${staleDays} day${staleDays === 1 ? "" : "s"} without follow-up (reminder only — pair with your CRM discipline). Header notifications can surface related nudges when enabled.`,
    scope: "user",
    status: "open",
    dueAt: null,
    requiresAck: false,
    dismissible: true,
    tone: "indigo",
    deepLink: { checklist_item_id: "daily-checklist" },
  };
}

export function mergeChecklistShellReminders(input: {
  notifications: readonly RawWorkplaceNotificationRow[];
  orgManagerNudge?: { title: string; body: string; sent_at: string } | null;
  hygieneStaleDays?: number | null;
}): WorkplaceReminderViewModel[] {
  const out: WorkplaceReminderViewModel[] = [];
  const seenNudge = new Set<string>();

  if (input.orgManagerNudge) {
    const vm = normalizeOrgManagerNudgeReminder(input.orgManagerNudge);
    out.push(vm);
    seenNudge.add(vm.title);
  }

  if (typeof input.hygieneStaleDays === "number" && input.hygieneStaleDays >= 1) {
    out.push(normalizeHygieneHintReminder(input.hygieneStaleDays));
  }

  for (const row of input.notifications) {
    const vm = normalizeWorkplaceReminderFromNotification(row);
    if (!vm) continue;
    if (vm.source === "org_nudge" && seenNudge.has(vm.title)) continue;
    if (vm.status !== "open") continue;
    out.push(vm);
  }

  return out;
}

export function filterRemindersForSurface(
  reminders: readonly WorkplaceReminderViewModel[],
  surface: "bell" | "checklist" | "lead",
  leadId?: string | null
): WorkplaceReminderViewModel[] {
  if (surface === "lead") {
    const lid = leadId?.trim();
    if (!lid) return [];
    return reminders.filter((r) => r.scope === "lead" && r.deepLink.lead_id === lid);
  }
  if (surface === "checklist") {
    return reminders.filter((r) => r.source !== "lead_reminder");
  }
  return [...reminders];
}

/** Personal / dismissible reminders never block; org forced-ack rows can when enabled. */
export function reminderBlocksDailyProgress(
  reminder: WorkplaceReminderViewModel,
  ackBlockingEnabled: boolean
): boolean {
  if (!ackBlockingEnabled) return false;
  if (reminder.dismissible) return false;
  if (reminder.source === "org_nudge" || reminder.source === "hygiene_hint" || reminder.source === "lead_hygiene") return false;
  return reminder.requiresAck && reminder.status === "open";
}

export function anyReminderBlocksDailyProgress(
  reminders: readonly WorkplaceReminderViewModel[],
  ackBlockingEnabled: boolean
): boolean {
  return reminders.some((r) => reminderBlocksDailyProgress(r, ackBlockingEnabled));
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}
